import { WordCloud } from '@antv/g2plot';
import { WordCloudOptions } from '@antv/g2plot/lib';

import theme from '../../../assets/theme';
export const WORDCLOUD_DIV = 'curipod-wordcloud';

export type Node = {
  id: string;
  count: number;
  size?: number[];
  isLeaf?: boolean;
  color?: string;
  label: string;
  cluster?: string;
  x?: number;
  y?: number;
  style?: React.CSSProperties;
  labelCfg?: { style: { fill: string; fontSize: number; fontWeight: string } };
};
const wordColors = [
  theme.colors.green,
  theme.colors.red,
  '#fcba03',
  theme.colors.orange,
  theme.colors.blue,
  '#666b20',
  theme.colors.blueDark,
  theme.colors.feideBlue,
  theme.colors.purple,
  theme.colors.feideBlue,
  '#851a91',
  '#40a19a',
];
const defaultConfig = {
  wordField: 'label',
  weightField: 'value',
  colorField: 'label',
  renderer: 'svg',
  theme: {
    colors10: wordColors,
    colors20: wordColors,
  },
  supportCSSTransform: true,
  autoFit: true,
  wordStyle: {
    fontFamily: 'Nunito',
    fontWeight: 'bold',
    rotation: 0,
    padding: 2,
  },
  random: () => 0.5,
  modes: {
    default: ['drag-canvas', { type: 'zoom-canvas', sensitivity: 0.4 }],
  },
};

const wordcloudFontSize = ({
  clientHeight,
  clientWidth,
  maxLength,
}: {
  maxLength: number;
  clientHeight?: number;
  clientWidth?: number;
}) => {
  const aspecRatio = clientHeight && clientWidth ? clientHeight / clientWidth : 0.3;
  // calculates the max font sized based on the pixel values from the letter 'æ'
  // the letter is 13.5 pixel wide on a fontsize of 15. And thus if one have maxLength
  // of characters of the letter æ. This formula gives the longest possible word to
  // be fitted inside the clientWidth (the wordcloud element).
  const maxFontSize =
    Math.max(
      Math.min(clientWidth ? (clientWidth * 15) / (11.5 * maxLength) : 10, 200),
      80,
      // Scale the maxFontsize to the clientHeight to make sure small screens always shows the screen even though.
      // the defautl aspect ratio is 0.50 in terms if height over width
      // so lower ratios should scale in percentage to the difference
    ) * Math.min(aspecRatio / 0.5, 1);
  return maxFontSize;
};

export const createWordCloud = ({
  nodes,
  config,
  clientWidth,
  onWordClick,
  wordcloudDivId,
  clientHeight,
}: {
  wordcloudDivId: string;
  nodes: Node[];
  config?: WordCloudOptions;
  clientWidth?: number | undefined;
  clientHeight?: number | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onWordClick?: (word: any) => void;
}) => {
  const hasMoreThan1Count = nodes.some(node => node.count > 1);

  // finding the words with the largest count scores
  const theWordsWiththeLargestScores = nodes.reduce((acc: Node[], current: Node) => {
    if (acc.length === 0) return [current];
    if (current.count > acc[0].count) return [current];
    if (current.count === acc[0].count) return [...acc, current];
    return acc;
  }, []);

  // using the words with the larges count value to find the maks length
  // as the longer sentences with smaller count
  // should not prevent the larges words to scale
  const maxlength = theWordsWiththeLargestScores.reduce(
    (acc, cur) => {
      return {
        more: cur.count > 1 && cur.label.length > acc.more ? cur.label.length : acc.more,
        one: cur.label.length > acc.one ? cur.label.length : acc.one,
      };
    },
    { one: 0, more: 0 },
  );

  const maxLength = maxlength.one;

  const maxFontSize = wordcloudFontSize({
    maxLength,
    clientHeight,
    clientWidth,
  });

  const minFontSize = maxFontSize / 2;

  const wordCloudConfig = {
    ...defaultConfig,
    ...config,
    ...(clientWidth && clientHeight
      ? { autoFit: false, width: clientWidth, height: clientHeight }
      : {}),

    data: nodes.map((n: Node) => ({ value: n.count, label: n.label })),
    wordStyle: {
      ...defaultConfig.wordStyle,
      fontSize: hasMoreThan1Count ? [minFontSize, maxFontSize] : maxFontSize,
      // when not having the hasMoreThan1Count all words will be equal size, and
      //  max fontSize will contain most of the words and still have the wordcloud
      // a decent size. using only minFontSize makes the cloud very small.
    },
  } as WordCloudOptions;
  const wordCloud = new WordCloud(wordcloudDivId, {
    ...wordCloudConfig,
  } as WordCloudOptions);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wordCloud.on('element:click', (item: any) => {
    const clickedNode = nodes.find((n: Node) => n.label === item.data.data.datum.label);
    if (clickedNode && onWordClick) {
      onWordClick(clickedNode);
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wordCloud.on('touch:start', (item: any) => {
    const clickedNode = nodes.find((n: Node) => n.label === item.data.data.datum.label);
    if (clickedNode && onWordClick) {
      onWordClick(clickedNode);
    }
  });
  return wordCloud;
};
